/**
 * Finished-league results share — compact URL snapshot (`/leagues?r=`).
 * Public card shows podium + roast awards only (no wallets / full table).
 */

import { isUsableAvatarUrl, resolveAvatarUrl } from "../../../utils/sessionUser";
import {
    formatLeagueRankLabel,
    leaguePrizeSplit,
    leagueTableRows,
} from "./leagueFormat";
import { leagueXpForRow } from "./leagueXp";
import { LEAGUE_REACTION_AWARD_CAP, leagueReactionCallouts } from "./leagueReactionStats";
import { isLeagueReactionEmoji, type LeagueReactionEmoji } from "./leagueReactions";
import type { League } from "./types";

/** Query on `/leagues` / `/leaguesdemo` for a public results card. */
export const LEAGUE_RESULTS_SHARE_QUERY = "r";

/** Short server codes (`POST /leagues/results-share`) — keep aligned with backend alphabet. */
export const LEAGUE_RESULTS_SHARE_CODE_RE = /^[23456789ABCDEFGHJKLMNPQRSTUVWXYZ]{8}$/i;

const MAX_NAME = 24;
const MAX_LEAGUE = 40;
const MAX_MATCH = 48;
/**
 * Short-code shares store avatars in DB JSON — allow long Google / upload URLs.
 * (Legacy inline `?r=` tokens still omit non-catalog URLs via `compactAvatarForWire`.)
 */
const MAX_AVATAR_URL = 2000;
const AVATAR_CDN_BASE = "https://cricket8-web-content.s3.amazonaws.com/avatars";

export function isLeagueResultsShareCode(value: string | null | undefined): boolean {
    return LEAGUE_RESULTS_SHARE_CODE_RE.test(String(value ?? "").trim());
}

export type LeagueResultsSharePodiumSeat = {
    name: string;
    rank: number;
    /** League points won (green total on the in-app podium). */
    points?: number;
    /** Prize coins for this place (coin footer on the podium card). */
    prize?: number;
    avatar?: string;
    /** Full-size avatar frame artwork (podium avatars are always large). */
    frame?: string;
};

export type LeagueResultsShareAward = {
    emoji: LeagueReactionEmoji;
    label: string;
    name: string;
    count: number;
    avatar?: string;
};

/** Per-game top scorer — used on the public hub podium recap. */
export type LeagueResultsShareGameWinner = {
    game: string;
    label: string;
    name: string;
};

/** Scale strip for the public hub podium recap. */
export type LeagueResultsShareStats = {
    players: number;
    plays: number;
    reactions?: number;
};

export type LeagueResultsShareSnapshot = {
    v: 1;
    leagueName: string;
    matchLabel: string;
    sharerName: string;
    sharerRank: number;
    sharerPrize?: number;
    sharerXp?: number;
    sharerAvatar?: string;
    sharerFrame?: string;
    podium: LeagueResultsSharePodiumSeat[];
    awards: LeagueResultsShareAward[];
    /** Public hub recap only — not required on personal share links. */
    gameWinners?: LeagueResultsShareGameWinner[];
    stats?: LeagueResultsShareStats;
};

type WireV1 = {
    v: 1;
    ln: string;
    m?: string;
    sn: string;
    sr: number;
    sp?: number;
    sx?: number;
    sav?: string;
    t: Array<{ n: string; rk: number; pts?: number; p?: number; av?: string }>;
    a?: Array<{ e: LeagueReactionEmoji; n: string; c: number; av?: string }>;
};

const AWARD_LABEL: Record<LeagueReactionEmoji, string> = {
    poop: "Most roasted",
    rofl: "Most laughed at",
    rocket: "Most hyped",
    fire: "Most on fire",
    clap: "Most respected",
    cry: "Most salt",
};

function clip(value: string, max: number): string {
    const s = String(value ?? "").trim().replace(/\s+/g, " ");
    if (s.length <= max) return s;
    return `${s.slice(0, Math.max(1, max - 1))}…`;
}

/**
 * Avatar URL for the in-app share card (full URL ok — not put in the query string when using short codes).
 */
export function clipShareAvatarUrl(raw: string | null | undefined): string | undefined {
    const resolved = resolveAvatarUrl({ avatar: raw });
    if (!isUsableAvatarUrl(resolved)) return undefined;
    let url = String(resolved).trim();
    if (url.startsWith("//")) url = `https:${url}`;
    else if (url.startsWith("/") && typeof window !== "undefined" && window.location?.origin) {
        url = `${window.location.origin}${url}`;
    }
    // Prefer full catalog PNG — WebP thumbs 403 as .png and don't paint in OG resvg.
    const catalog = url.match(
        /\/avatars\/(?:thumbs\/(?:\d+\/)?)?([a-z0-9-]+)\.(?:webp|png|jpe?g)(?:\?|$)/i,
    );
    if (catalog?.[1]) {
        url = `${AVATAR_CDN_BASE}/${catalog[1].toLowerCase()}.png`;
    }
    const okAbsolute = /^https?:\/\//i.test(url);
    const okRelative = url.startsWith("/");
    if (!okAbsolute && !okRelative) return undefined;
    if (url.length > MAX_AVATAR_URL) return undefined;
    return url;
}

/** Frame artwork URL for short-code snapshots (absolute https only; legacy inline tokens omit frames). */
export function clipShareFrameUrl(raw: string | null | undefined): string | undefined {
    const url = String(raw ?? "").trim();
    if (!/^https:\/\//i.test(url) || url.length > MAX_AVATAR_URL) return undefined;
    return url;
}

/** Catalog key for legacy inline tokens — `giraffe-orange` instead of a full CDN URL. */
function compactAvatarForWire(url: string | undefined): string | undefined {
    if (!url) return undefined;
    const m = String(url).match(/\/avatars\/(?:thumbs\/(?:\d+\/)?)?([a-z0-9-]+)\.(?:png|webp|jpe?g)(?:\?|$)/i);
    if (m?.[1]) return m[1].toLowerCase();
    return undefined;
}

function expandAvatarFromWire(raw: string | undefined): string | undefined {
    const value = String(raw ?? "").trim();
    if (!value) return undefined;
    if (/^https?:\/\//i.test(value) || value.startsWith("/")) return clipShareAvatarUrl(value);
    if (/^[a-z0-9-]+$/i.test(value)) return `${AVATAR_CDN_BASE}/${value.toLowerCase()}.png`;
    return undefined;
}

function matchLabelFromLeague(league: League): string {
    const raw = String(league.fixture?.name ?? "").trim();
    if (!raw) return "";
    return clip(raw.split(",")[0]!.trim() || raw, MAX_MATCH);
}

/** Build a public snapshot from a finished league for the current viewer. */
export function buildLeagueResultsShareSnapshot(
    league: League,
    viewerUserId?: number | null,
): LeagueResultsShareSnapshot | null {
    if (league.status !== "ended") return null;
    const rows = leagueTableRows(league);
    if (rows.length === 0) return null;

    const prizes = leaguePrizeSplit(league);
    const viewerId = viewerUserId != null ? Number(viewerUserId) : NaN;
    const me =
        (Number.isFinite(viewerId) ? rows.find((row) => row.userId === viewerId) : null) ??
        rows.find((row) => row.isViewer) ??
        rows[0]!;

    const podium: LeagueResultsSharePodiumSeat[] = rows.slice(0, 3).map((row) => {
        const prize = row.rank >= 1 && row.rank <= prizes.length ? prizes[row.rank - 1] : undefined;
        const seat: LeagueResultsSharePodiumSeat = {
            name: clip(row.playerName, MAX_NAME),
            rank: row.rank,
        };
        const points = Math.round(Number(row.wonPoints) || 0);
        if (points > 0) seat.points = points;
        if (prize != null && prize > 0) seat.prize = Math.round(prize);
        const avatar = clipShareAvatarUrl(row.avatar);
        if (avatar) seat.avatar = avatar;
        const frame = clipShareFrameUrl(row.avatarFrameUrl);
        if (frame) seat.frame = frame;
        return seat;
    });

    const awards: LeagueResultsShareAward[] = leagueReactionCallouts(league)
        .slice(0, LEAGUE_REACTION_AWARD_CAP)
        .map((callout) => {
            const award: LeagueResultsShareAward = {
                emoji: callout.emoji,
                label: callout.label,
                name: clip(callout.member.playerName, MAX_NAME),
                count: callout.count,
            };
            const avatar = clipShareAvatarUrl(callout.member.avatar);
            if (avatar) award.avatar = avatar;
            return award;
        });

    const sharerPrize =
        me.rank >= 1 && me.rank <= prizes.length ? prizes[me.rank - 1] : undefined;
    const sharerXp = leagueXpForRow(league, me);
    const sharerAvatar = clipShareAvatarUrl(me.avatar);
    const sharerFrame = clipShareFrameUrl(me.avatarFrameUrl);

    const snap: LeagueResultsShareSnapshot = {
        v: 1,
        leagueName: clip(league.name || "Cricket8 League", MAX_LEAGUE),
        matchLabel: matchLabelFromLeague(league),
        sharerName: clip(me.playerName, MAX_NAME),
        sharerRank: me.rank,
        podium,
        awards,
    };
    if (sharerPrize != null && sharerPrize > 0) snap.sharerPrize = Math.round(sharerPrize);
    if (sharerXp > 0) snap.sharerXp = Math.round(sharerXp);
    if (sharerAvatar) snap.sharerAvatar = sharerAvatar;
    if (sharerFrame) snap.sharerFrame = sharerFrame;
    return snap;
}

function toWire(snap: LeagueResultsShareSnapshot): WireV1 {
    const wire: WireV1 = {
        v: 1,
        ln: snap.leagueName,
        sn: snap.sharerName,
        sr: snap.sharerRank,
        t: snap.podium.map((seat) => {
            const row: { n: string; rk: number; pts?: number; p?: number; av?: string } = {
                n: seat.name,
                rk: seat.rank,
            };
            if (seat.points != null && seat.points > 0) row.pts = seat.points;
            if (seat.prize != null && seat.prize > 0) row.p = seat.prize;
            const av = compactAvatarForWire(seat.avatar);
            if (av) row.av = av;
            return row;
        }),
    };
    if (snap.matchLabel) wire.m = snap.matchLabel;
    if (snap.sharerPrize != null && snap.sharerPrize > 0) wire.sp = snap.sharerPrize;
    if (snap.sharerXp != null && snap.sharerXp > 0) wire.sx = snap.sharerXp;
    const sav = compactAvatarForWire(snap.sharerAvatar);
    if (sav) wire.sav = sav;
    if (snap.awards.length > 0) {
        wire.a = snap.awards.map((a) => {
            const row: { e: LeagueReactionEmoji; n: string; c: number; av?: string } = {
                e: a.emoji,
                n: a.name,
                c: a.count,
            };
            const av = compactAvatarForWire(a.avatar);
            if (av) row.av = av;
            return row;
        });
    }
    return wire;
}

function fromWire(raw: unknown): LeagueResultsShareSnapshot | null {
    if (!raw || typeof raw !== "object") return null;
    const o = raw as Record<string, unknown>;
    if (Number(o.v) !== 1) return null;
    const leagueName = clip(String(o.ln ?? ""), MAX_LEAGUE);
    const sharerName = clip(String(o.sn ?? ""), MAX_NAME);
    const sharerRank = Math.max(0, Math.floor(Number(o.sr) || 0));
    if (!leagueName || !sharerName || sharerRank < 1) return null;

    const top = Array.isArray(o.t) ? o.t : [];
    const podium: LeagueResultsSharePodiumSeat[] = [];
    for (const item of top.slice(0, 3)) {
        if (!item || typeof item !== "object") continue;
        const row = item as Record<string, unknown>;
        const name = clip(String(row.n ?? ""), MAX_NAME);
        const rank = Math.max(0, Math.floor(Number(row.rk) || 0));
        if (!name || rank < 1) continue;
        const seat: LeagueResultsSharePodiumSeat = { name, rank };
        const points = Math.floor(Number(row.pts) || 0);
        if (points > 0) seat.points = points;
        const prize = Math.floor(Number(row.p) || 0);
        if (prize > 0) seat.prize = prize;
        const avatar = expandAvatarFromWire(typeof row.av === "string" ? row.av : undefined);
        if (avatar) seat.avatar = avatar;
        podium.push(seat);
    }
    if (podium.length === 0) return null;

    const awards: LeagueResultsShareAward[] = [];
    if (Array.isArray(o.a)) {
        for (const item of o.a.slice(0, LEAGUE_REACTION_AWARD_CAP)) {
            if (!item || typeof item !== "object") continue;
            const row = item as Record<string, unknown>;
            const emoji = row.e;
            if (!isLeagueReactionEmoji(emoji)) continue;
            const name = clip(String(row.n ?? ""), MAX_NAME);
            const count = Math.max(0, Math.floor(Number(row.c) || 0));
            if (!name || count < 1) continue;
            const award: LeagueResultsShareAward = {
                emoji,
                label: AWARD_LABEL[emoji],
                name,
                count,
            };
            const avatar = expandAvatarFromWire(typeof row.av === "string" ? row.av : undefined);
            if (avatar) award.avatar = avatar;
            awards.push(award);
        }
    }

    const snap: LeagueResultsShareSnapshot = {
        v: 1,
        leagueName,
        matchLabel: clip(String(o.m ?? ""), MAX_MATCH),
        sharerName,
        sharerRank,
        podium,
        awards,
    };
    const sp = Math.floor(Number(o.sp) || 0);
    const sx = Math.floor(Number(o.sx) || 0);
    if (sp > 0) snap.sharerPrize = sp;
    if (sx > 0) snap.sharerXp = sx;
    const sharerAvatar = expandAvatarFromWire(typeof o.sav === "string" ? o.sav : undefined);
    if (sharerAvatar) snap.sharerAvatar = sharerAvatar;
    return snap;
}

function bytesToBase64Url(bytes: Uint8Array): string {
    let bin = "";
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
    const b64 =
        typeof btoa === "function"
            ? btoa(bin)
            : Buffer.from(bytes).toString("base64");
    return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function base64UrlToBytes(token: string): Uint8Array | null {
    const padded = token.replace(/-/g, "+").replace(/_/g, "/");
    const pad = padded.length % 4 === 0 ? "" : "=".repeat(4 - (padded.length % 4));
    try {
        if (typeof atob === "function") {
            const bin = atob(padded + pad);
            const out = new Uint8Array(bin.length);
            for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
            return out;
        }
        return new Uint8Array(Buffer.from(padded + pad, "base64"));
    } catch {
        return null;
    }
}

export function encodeLeagueResultsShareToken(snapshot: LeagueResultsShareSnapshot): string {
    const json = JSON.stringify(toWire(snapshot));
    if (typeof TextEncoder !== "undefined") {
        return bytesToBase64Url(new TextEncoder().encode(json));
    }
    return bytesToBase64Url(Uint8Array.from(Buffer.from(json, "utf8")));
}

export function decodeLeagueResultsShareToken(token: string): LeagueResultsShareSnapshot | null {
    const raw = String(token ?? "").trim();
    if (!raw || raw.length > 6000) return null;
    const bytes = base64UrlToBytes(raw);
    if (!bytes || bytes.length === 0) return null;
    try {
        const json =
            typeof TextDecoder !== "undefined"
                ? new TextDecoder().decode(bytes)
                : Buffer.from(bytes).toString("utf8");
        return fromWire(JSON.parse(json));
    } catch {
        return null;
    }
}

export function leagueResultsSharePath(token: string, basePath = "/leagues"): string {
    const trimmed = String(token ?? "").trim();
    if (!trimmed) return basePath;
    const params = new URLSearchParams();
    params.set(LEAGUE_RESULTS_SHARE_QUERY, trimmed);
    return `${basePath}?${params}`;
}

export function readLeagueResultsShareTokenFromSearch(search: string): string | null {
    const raw = search.startsWith("?") ? search.slice(1) : search;
    const value = new URLSearchParams(raw).get(LEAGUE_RESULTS_SHARE_QUERY);
    return value?.trim() ? value.trim() : null;
}

export type FinishLineVoice = "I" | "name" | "you";

/** Podium finish line — share uses I/name; in-app member view keeps "You". */
export function formatLeagueResultsFinishLine(
    snapshot: Pick<LeagueResultsShareSnapshot, "sharerName" | "sharerRank" | "sharerPrize">,
    voice: FinishLineVoice,
): string {
    const rank = formatLeagueRankLabel(snapshot.sharerRank).toLowerCase();
    const who =
        voice === "I" ? "I" : voice === "you" ? "You" : clip(snapshot.sharerName, MAX_NAME) || "They";
    const won =
        snapshot.sharerPrize != null && snapshot.sharerPrize > 0
            ? ` and won ${Math.round(snapshot.sharerPrize).toLocaleString()} gold balls`
            : "";
    return `${who} finished ${rank}${won}!`;
}

/** Leagues are often named after their fixture — don't repeat it. */
function distinctMatchLabel(snapshot: Pick<LeagueResultsShareSnapshot, "leagueName" | "matchLabel">): string {
    const match = String(snapshot.matchLabel ?? "").trim();
    if (!match) return "";
    return match.toLowerCase() === String(snapshot.leagueName ?? "").trim().toLowerCase() ? "" : match;
}

/** "League name · Match" line under "League finished!". */
export function leagueResultsShareSubtitle(
    snapshot: Pick<LeagueResultsShareSnapshot, "leagueName" | "matchLabel">,
): string {
    return [snapshot.leagueName, distinctMatchLabel(snapshot)].filter(Boolean).join(" · ");
}

/**
 * Messages typed above the link, by finish. The preview card already carries the result
 * (image + title), so these are short challenges that never repeat rank, coins or league.
 */
export const LEAGUE_RESULTS_SHARE_TEXTS = {
    winner: [
        "Top of the podium 👑 Who's taking my crown?",
        "Champion 🏆 Come and have a go if you think you're good enough.",
        "Won it 🥇 Fancy a rematch?",
        "Undefeated 😎 Next match, you and me?",
    ],
    podium: [
        "Think you can beat me? 🏏",
        "So close to the top 😤 Rematch, anyone?",
        "Podium finish 🔥 Your move.",
        "Next time that crown's mine 👑 Who's in?",
    ],
    rest: [
        "Think you can beat me? 🏏",
        "I'll get you next time 😤 Who's up for a rematch?",
        "Not my day… fancy a rematch? 😅",
        "Bring it on next match 🏏 Who's in?",
    ],
} as const;

export function buildLeagueResultsShareText(
    snapshot: Pick<LeagueResultsShareSnapshot, "sharerRank">,
    random: () => number = Math.random,
): string {
    const rank = snapshot.sharerRank;
    const pool =
        rank === 1
            ? LEAGUE_RESULTS_SHARE_TEXTS.winner
            : rank === 2 || rank === 3
              ? LEAGUE_RESULTS_SHARE_TEXTS.podium
              : LEAGUE_RESULTS_SHARE_TEXTS.rest;
    return pool[Math.min(pool.length - 1, Math.floor(random() * pool.length))]!;
}

export function buildLeagueResultsShareTitle(snapshot: LeagueResultsShareSnapshot): string {
    const rank = formatLeagueRankLabel(snapshot.sharerRank).toLowerCase();
    const league = snapshot.leagueName || "a Cricket8 league";
    return `${snapshot.sharerName} finished ${rank} in ${league}`;
}

export const LEAGUE_RESULTS_OG_DESCRIPTION = "Create a league and battle your friends on every match.";

export function buildLeagueResultsOgDescription(_snapshot: LeagueResultsShareSnapshot): string {
    return LEAGUE_RESULTS_OG_DESCRIPTION;
}
